import { Plugin, NodeSelection } from '@remirror/pm/state';
import { isElementDomNode, findParentNodeOfType } from '@remirror/core';
import { isNodeSlice, nodeAtCoords, nearestBlockFromCoords, getClosestSide, nodePosAtDOM } from '../utils';

export function dropHandler(options) {
  return new Plugin({
    props: {
      handleDrop: (view, event, slice, moved) => {
        const isNode = isNodeSlice(slice);
        if (!isNode) {
          return false;
        }

        document.querySelector('.ProseMirror').classList.remove('dragging');

        const tr = view.state.tr;
        const coords = { x: event.clientX, y: event.clientY };
        const lastNode = document.querySelector('.ProseMirror')?.lastElementChild;
        const lastNodeRect = lastNode?.getBoundingClientRect();

        if (lastNodeRect && event.clientY > lastNodeRect.bottom) {
          try {
            view.dragging = null;
            const currentState = view.state;
            const docEnd = currentState.doc.content.size;
            let insertTr = currentState.tr;

            if (moved) {
              insertTr.deleteSelection();
              const mappedDocEnd = insertTr.mapping.map(docEnd);
              insertTr.insert(mappedDocEnd, slice.content.firstChild);
            } else {
              insertTr.insert(docEnd, slice.content.firstChild);
            }

            view.dispatch(insertTr.setMeta('uiEvent', 'drop'));
            document.querySelector('.ProseMirror').classList.remove('dragging');
            view.focus();
            return true;
          } catch (e) {
            console.error('Drop handling error', e);
            view.dragging = null;
            document.querySelector('.ProseMirror').classList.remove('dragging');
            return false;
          }
        }

        let targetNodeDOM = nodeAtCoords(coords, options.isDraggableBlock);
        let closestSide = targetNodeDOM && getClosestSide(targetNodeDOM, coords);
        if (!targetNodeDOM) {
          const { nearestBlock, side } = nearestBlockFromCoords(coords);
          targetNodeDOM = nearestBlock;
          closestSide = side;
        }

        if (!targetNodeDOM || !isElementDomNode(targetNodeDOM)) {
          return true;
        }

        const targetNodePos = nodePosAtDOM(targetNodeDOM, view);
        const targetNode = view.state.doc.nodeAt(targetNodePos);

        if (tr.selection.from === targetNodePos) {
          return true;
        }

        if (moved) {
          tr.deleteSelection();
        }

        let pos;
        if (closestSide === 'top' || closestSide === 'bottom') {
          pos = tr.mapping.map(targetNodePos + (closestSide === 'bottom' ? targetNode.nodeSize : 0));
        } else {
          const nodeTypes = view.state.schema.nodes;

          if (!nodeTypes.column || !nodeTypes.columns) {
            console.error('Error: Column or Columns node type is missing from schema.');
            return false;
          }

          const columnNode = findParentNodeOfType({
            types: nodeTypes.column,
            selection: NodeSelection.create(view.state.doc, targetNodePos),
          });

          if (columnNode) {
            const newColumn = nodeTypes.column.create(null, null);
            const newColumnPos =
              closestSide === 'left' ? tr.mapping.map(columnNode.pos) : tr.mapping.map(columnNode.end);
            tr.replaceRangeWith(newColumnPos, newColumnPos, newColumn);

            pos = newColumnPos + 1;
          } else {
            const targetNodeColumn = nodeTypes.column.create({ scale: 1 / 2 }, targetNode);
            const newColumns =
              closestSide === 'left'
                ? [nodeTypes.column.create({ scale: 1 / 2 }, null), targetNodeColumn]
                : [targetNodeColumn, nodeTypes.column.create({ scale: 1 / 2 }, null)];

            tr.replaceRangeWith(
              tr.mapping.map(targetNodePos),
              tr.mapping.map(targetNodePos + targetNode.nodeSize),
              nodeTypes.columns.create(null, newColumns)
            );

            pos = tr.mapping.map(targetNodePos) + (closestSide === 'right' ? targetNodeColumn.nodeSize : 0) + 2;
          }
        }

        const content =
          slice.content.firstChild.type.name === 'column'
            ? [...slice.content.firstChild.content.content]
            : slice.content.firstChild;

        tr.replaceRangeWith(pos, pos, content);
        tr.setSelection(NodeSelection.create(tr.doc, pos));
        view.focus();
        view.dispatch(tr.setMeta('uiEvent', 'drop'));

        return true;
      },
    },
  });
}
